import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { MatTabChangeEvent, MatTabsModule } from '@angular/material/tabs';
import { DataSeries } from 'src/app/models/classes/data-series.class';
import { KeyValuePair } from 'src/app/models/classes/key-value-pair.class';
import { CurrencyFormatPipe } from 'src/app/pipes/currency-format.pipe';
import { ActivityTrackingService } from 'src/app/services/activity-tracking.service';
import { GROSVENOR_BLUE, LIGHT_GREY, WHITE } from '@gcm/gcm-ui-angular';
import { HighchartsService } from 'src/app/services/highcharts-service.service';
import { BaseGeographyWidgetComponent } from 'src/app/shared/base-geography-widget/base-geography-widget.component';
import { BaseWidgetComponent } from 'src/app/shared/base-widget/base-widget.component';
import { GcmChartComponent } from 'src/app/shared/gcm-chart/gcm-chart.component';
import { InfoIconComponent } from 'src/app/shared/info-icon/info-icon.component';

interface RemainingValueTabConfig {
  id: 'geography' | 'sectors' | 'assetStatus';
  label: string;
  tooltip: string;
  type: 'geography' | 'chart';
  chartDataCy?: string;
  widgetKey?: string;
  chartType?: 'bar' | 'pie';
  chartData?: DataSeries;
  chartOptions?: Highcharts.Options | null;
  error?: string;
}

@Component({
  selector: 'app-remaining-value-widget',
  imports: [
    MatTabsModule,
    BaseGeographyWidgetComponent,
    InfoIconComponent,
    BaseWidgetComponent,
    GcmChartComponent
  ],
  templateUrl: './remaining-value-widget.component.html',
  styleUrls: ['./remaining-value-widget.component.scss']
})
export class RemainingValueWidgetComponent {
  private highchartsService = inject(HighchartsService);
  private currencyFormat = inject(CurrencyFormatPipe);
  private activityTracking = inject(ActivityTrackingService);

  readonly widgets = input<Record<string, KeyValuePair<string | number, number>[]>>({ remainingValueBySector: [], remainingValueByMapRegion: [], remainingValueByCountry: [] });
  readonly otherSectorNames = input<string[]>([]);
  readonly currency = input<string | undefined>(undefined);
  readonly displayTabs = input<RemainingValueTabConfig['id'][] | undefined>(undefined);
  readonly showUSMapWidget = input<boolean>(false);
  readonly otherCountryNames = input<string[]>([]);

  chartStyle: Record<string, string> = { width: '99%', display: 'flex' };

  tabs = signal<RemainingValueTabConfig[]>([
    {
      id: 'geography',
      label: 'Geography',
      tooltip: 'Primary region in which underlying assets operate',
      type: 'geography'
    },
    {
      id: 'sectors',
      label: 'Sectors',
      tooltip: 'GICS Sector Classification for underlying assets',
      type: 'chart',
      chartDataCy: 'sectorsChart',
      widgetKey: 'remainingValueBySector',
      chartType: 'bar',
      chartData: undefined,
      chartOptions: null,
      error: ''
    },
    {
      id: 'assetStatus',
      label: 'Private/Public',
      tooltip: 'Private/public status of underlying assets',
      type: 'chart',
      chartDataCy: 'assetStatusChart',
      widgetKey: 'remainingValueByAssetStatus',
      chartType: 'pie',
      chartData: undefined,
      chartOptions: null,
      error: ''
    }
  ]);

  visibleTabs = computed(() => {
    const display = this.displayTabs();
    if (!display) return this.tabs();
    return this.tabs().filter(tab => display.includes(tab.id));
  });

  private readonly rebuildTabs = effect(() => {
    const widgets = this.widgets();
    if (widgets) {
      this.tabs.update(tabs => tabs.map(tab => {
        if (tab.type !== 'chart') return tab;

        const rawData = (tab.widgetKey ? widgets[tab.widgetKey] : undefined) ?? [];
        const data = tab.id === 'sectors' ? rawData.slice(0, 10) : rawData;
        const chartData = this.highchartsService.buildDataSeries(data, 'Remaining Market Value');
        const sum = rawData.map((a: { value: number }) => a.value).reduce((total: number, val: number) => total + val, 0);

        return {
          ...tab,
          chartData,
          error: sum > 0 ? '' : this.highchartsService.noResultsText,
          chartOptions: tab.id === 'sectors'
            ? this.buildRemainingValueBySectorChartOptions(sum)
            : this.buildRemainingValueByAssetStatusChartOptions(sum)
        };
      }));
    }
  });

  private buildRemainingValueBySectorChartOptions(sum: number) {
    if (sum <= 0) return null;

    const currencyFormat = this.currencyFormat;
    const currency = this.currency;
    const otherSectorNames = this.otherSectorNames;

    return {
      chart: { height: 360 },
      plotOptions: {
        series: {
          dataLabels: {
            formatter: function (this: Highcharts.Point) {
              return ((Math.abs(this.y as number) / Number(sum)) * 100).toFixed(1) + '%';
            }
          }
        }
      },
      tooltip: {
        formatter: function (this: Highcharts.Point) {
          const valToShow = currencyFormat.transform(this.y as number, currency());
          const data = `${this.series.name} <br/>` + valToShow + ` <br/><hr class="mt-2 mb-2 mr-0 ml-0" style="background-color: ${WHITE}">`
          const footer = this.key === 'Other' && otherSectorNames().length > 0 ? `<div>${otherSectorNames().join(', ')}</div>` : `<div>${this.key}</div>`
          return data + footer
        }
      },
      series: [{ color: GROSVENOR_BLUE }]
    } as Highcharts.Options;
  }

  private buildRemainingValueByAssetStatusChartOptions(sum: number) {
    if (sum <= 0) return null;

    const currencyFormat = this.currencyFormat;
    const currency = this.currency;

    return {
      chart: { height: 320, type: 'pie', marginLeft: 50, marginRight: 50 },
      plotOptions: {
        pie: { size: '90%', innerSize: '65%' },
        series: {
          dataLabels: {
            formatter: function (this: Highcharts.Point) {
              return `${this.key} <br/>` + ((Math.abs(this.y as number) / Number(sum)) * 100).toFixed(1) + '%';
            },
            padding: 0,
            distance: 10,
            alignTo: 'plotEdges'
          },
          states: { hover: { halo: null } }
        }
      },
      tooltip: {
        formatter: function (this: Highcharts.Point) {
          const valToShow = currencyFormat.transform(this.y as number, currency());
          const data = `${this.series.name} <br/>` + valToShow + ` <br/><hr class="mt-2 mb-2 mr-0 ml-0" style="background-color: ${WHITE}">`
          return data + `<div>${this.key}</div>`
        }
      },
      series: [{ allowPointSelect: false, colors: [GROSVENOR_BLUE, LIGHT_GREY] }]
    } as Highcharts.Options;
  }

  handleChange(event: MatTabChangeEvent) {
    const tab = this.visibleTabs()[event.index];
    if (tab) {
      this.activityTracking.widgetTabChange('Remaining value widget', tab.label);
    }
  }
}
